import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessResolver } from "@/hooks/useAccessResolver";
import { routeFromAccess } from "@/lib/routeFromAccess";
import { getInviteToken } from "@/lib/inviteTokenStorage";
import { hasSupabaseAuthCallbackParams } from "@/lib/supabaseAuthCallback";
import { AccessErrorScreen } from "@/components/AccessErrorScreen";
import { InviteInvalidScreen } from "@/components/InviteInvalidScreen";
import { useProperties } from "@/hooks/useProperties";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { supabase } from "@/integrations/supabase/client";
import Landing from "./Landing";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading, refetchProfile, signOut } = useAuth();
  const { data: access, isLoading: accessLoading } = useAccessResolver();
  const hasNavigatedRef = useRef(false);

  const { data: properties, isLoading: propertiesLoading } = useProperties();
  const { data: cleaner, isLoading: cleanerLoading } = useCleanerRecord();
  const [hasAssignedTasks, setHasAssignedTasks] = useState<boolean | null>(null);
  const [isCheckingTasks, setIsCheckingTasks] = useState(false);

  useEffect(() => {
    const checkAssignedTasks = async () => {
      if (!cleaner || cleanerLoading || isCheckingTasks) return;

      setIsCheckingTasks(true);
      try {
        const { data: tasks, error } = await supabase
          .from("cleaning_tasks")
          .select("id")
          .eq("cleaner_id", cleaner.id)
          .limit(1);

        if (error) {
          console.error("[Index] Error checking assigned tasks:", error);
          setHasAssignedTasks(false);
        } else {
          setHasAssignedTasks((tasks?.length ?? 0) > 0);
        }
      } catch (error) {
        console.error("[Index] Error checking assigned tasks:", error);
        setHasAssignedTasks(false);
      } finally {
        setIsCheckingTasks(false);
      }
    };

    if (cleaner && !cleanerLoading) {
      checkAssignedTasks();
    } else if (!cleaner && !cleanerLoading) {
      setHasAssignedTasks(false);
    }
  }, [cleaner, cleanerLoading, isCheckingTasks]);

  useEffect(() => {
    // Only PKCE/hash auth payloads belong on /auth/callback. A leftover invite token
    // or #demo hash used to bounce signed-out users into an infinite Loading loop.
    if (hasSupabaseAuthCallbackParams(location.search, location.hash)) {
      navigate(`/auth/callback${location.search || ""}${location.hash || ""}`, { replace: true });
      return;
    }

    if (!loading && !user) {
      return;
    }

    if (!loading && user && profile && location.pathname === "/") {
      if (!accessLoading) {
        const route = routeFromAccess(access, accessLoading);
        let nextRoute = route;
        if (
          access?.type === "full_dashboard" &&
          !access.workspaceLock &&
          access.role === "owner" &&
          cleaner
        ) {
          try {
            if (localStorage.getItem("view_context") === "cleaner") {
              nextRoute = "/worker/schedule";
            }
          } catch {
            /* ignore */
          }
        }
        if (nextRoute && nextRoute !== location.pathname) {
          if (!hasNavigatedRef.current) {
            hasNavigatedRef.current = true;
            navigate(nextRoute, { replace: true });
          }
        } else if (access?.type === "no_access") {
          const inviteTokenNoAccess = getInviteToken();
          if (inviteTokenNoAccess) {
            navigate(`/invite?token=${encodeURIComponent(inviteTokenNoAccess)}`, { replace: true });
            return;
          }
          const isLoadingFirstTimeCheck =
            propertiesLoading || cleanerLoading || isCheckingTasks || hasAssignedTasks === null;
          if (!isLoadingFirstTimeCheck) {
            const hasProperties = (properties?.length ?? 0) > 0;
            const hasTasks = hasAssignedTasks === true;
            if (!hasProperties && !hasTasks) {
              if (profile?.role === "owner") {
                navigate("/owner/properties", { replace: true });
                return;
              }
              if (profile?.role === "cleaner") {
                navigate("/worker/schedule", { replace: true });
                return;
              }
              navigate("/onboarding/decision", { replace: true });
              return;
            }
          }
        } else if (access != null) {
          navigate("/login", { replace: true });
        }
      }
    }
  }, [
    user,
    profile,
    loading,
    access,
    accessLoading,
    navigate,
    location.pathname,
    location.search,
    location.hash,
    properties,
    propertiesLoading,
    cleaner,
    cleanerLoading,
    hasAssignedTasks,
    isCheckingTasks,
  ]);

  if (!loading && !user) {
    return <Landing />;
  }

  if (!loading && user && profile && access?.type === "invite_invalid") {
    return <InviteInvalidScreen reason={access.reason} />;
  }

  const isLoadingFirstTimeCheck =
    propertiesLoading || cleanerLoading || isCheckingTasks || hasAssignedTasks === null;
  const hasProperties = (properties?.length ?? 0) > 0;
  const hasTasks = hasAssignedTasks === true;
  const isFirstTimeUser = !isLoadingFirstTimeCheck && !hasProperties && !hasTasks;

  if (!loading && user && profile && access?.type === "no_access" && !isFirstTimeUser && !isLoadingFirstTimeCheck) {
    return <AccessErrorScreen />;
  }

  if (!loading && user && profile && access?.type === "unauthorized") {
    return <AccessErrorScreen />;
  }

  if (!loading && user && !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background p-4">
        <p className="text-muted-foreground text-center">Couldn&apos;t load your account.</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refetchProfile()}
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => signOut()}
            className="rounded-md border border-input bg-background px-4 py-2"
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p>Loading...</p>
    </div>
  );
};

export default Index;
